/**
 * SoleadLab 2.0 - Geometry Utilities
 *
 * Pure geometry helpers for indication rectangles, coordinate conversion,
 * and polygon rotation. Matches original editor (23).html behavior.
 */

/** Convert width/height in meters to degree offsets at a given latitude */
export function metersToDeg(
  wMeters: number,
  hMeters: number,
  lat: number
): { dLng: number; dLat: number } {
  const mpdLat = 111320;
  const mpdLng = 111320 * Math.cos((lat * Math.PI) / 180);
  return { dLng: wMeters / mpdLng, dLat: hMeters / mpdLat };
}

/** Create a rectangle polygon (GeoJSON) centered on [lng, lat] with size in meters */
export function rectGeomFromCenter(
  lng: number,
  lat: number,
  wMeters: number,
  hMeters: number
): GeoJSON.Polygon {
  const { dLng, dLat } = metersToDeg(wMeters, hMeters, lat);
  return {
    type: 'Polygon',
    coordinates: [
      [
        [lng - dLng / 2, lat - dLat / 2],
        [lng + dLng / 2, lat - dLat / 2],
        [lng + dLng / 2, lat + dLat / 2],
        [lng - dLng / 2, lat + dLat / 2],
        [lng - dLng / 2, lat - dLat / 2],
      ],
    ],
  };
}

/** Get the default width x height (in meters) for a given indication type.
 *  For 'String OC', width = 1m × moduleCount (each module ≈ 1m wide). */
export function defaultSizeForType(type: string, moduleCount?: number): { w: number; h: number } {
  if (type === 'String OC') {
    const count = moduleCount && moduleCount > 0 ? moduleCount : 10;
    return { w: count, h: 2 };
  }
  return { w: 1, h: 2 };
}

/** Compute centroid of a polygon ring (excluding closing vertex) */
export function centroidOfPolygon(
  coords: number[][]
): [number, number] | null {
  if (!coords || coords.length < 4) return null;
  const pts = coords.slice(0, coords.length - 1);
  let sx = 0,
    sy = 0;
  for (const [x, y] of pts) {
    sx += x;
    sy += y;
  }
  return [sx / pts.length, sy / pts.length];
}

/** Great-circle distance between two [lng,lat] points, in meters.
 *  Haversine — accurate at any earth scale. (Moved from missionValidation,
 *  which re-exports it, so guide-line navigation can share the same copy.) */
export function haversineM(a: [number, number], b: [number, number]): number {
  const R = 6_371_000;
  const lat1 = (a[1] * Math.PI) / 180;
  const lat2 = (b[1] * Math.PI) / 180;
  const dLat = lat2 - lat1;
  const dLng = ((b[0] - a[0]) * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Initial great-circle bearing from a to b, degrees clockwise from north [0, 360). */
export function bearingDeg(a: [number, number], b: [number, number]): number {
  const lat1 = (a[1] * Math.PI) / 180;
  const lat2 = (b[1] * Math.PI) / 180;
  const dLng = ((b[0] - a[0]) * Math.PI) / 180;
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  const deg = (Math.atan2(y, x) * 180) / Math.PI;
  return (deg + 360) % 360;
}

/** 8-wind compass sector label for a bearing in degrees (N, NE, E, …). */
export function compassSector(deg: number): string {
  const sectors = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  return sectors[Math.round((((deg % 360) + 360) % 360) / 45) % 8];
}

/** Representative lng/lat for an indication geometry (Point coords / polygon centroid). */
export function indCenter(g: GeoJSON.Geometry): [number, number] | null {
  if (g.type === 'Point') return g.coordinates as [number, number];
  if (g.type === 'Polygon') return centroidOfPolygon(g.coordinates[0] as [number, number][]) ?? null;
  return null;
}

/** Rotate a polygon geometry around a center point by angleDeg degrees */
export function rotatePolygonAroundCenter(
  geom: GeoJSON.Polygon,
  center: [number, number],
  angleDeg: number
): GeoJSON.Polygon {
  if (!angleDeg) return geom;
  const [clng, clat] = center;
  const mpdLng = 111320 * Math.cos((clat * Math.PI) / 180);
  const mpdLat = 111320;
  const angle = (angleDeg * Math.PI) / 180;
  return {
    type: 'Polygon',
    coordinates: [
      geom.coordinates[0].map(([lng, lat]) => {
        const dx = (lng - clng) * mpdLng;
        const dy = (lat - clat) * mpdLat;
        return [
          clng + (dx * Math.cos(angle) - dy * Math.sin(angle)) / mpdLng,
          clat + (dx * Math.sin(angle) + dy * Math.cos(angle)) / mpdLat,
        ] as [number, number];
      }),
    ],
  };
}
